import noop from "lodash/noop";
import React, { createContext, useContext, useMemo, useState } from "react";
import { RelatedProductsSidebarInterface } from "web/react/components/related-products-sidebar/related-products-sidebar";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useProductSelector } from "web/react/hooks/use-product-selector/use-product-selector";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import { Schemas } from "web/types/examples/schemas";
import {
    BuyOptionSerializer,
    DesignerSerializer,
    ProductSerializer,
    BuyBuyBuyAreaSize as SizeOption,
    SizeSerializer,
} from "web/types/serializers";
import { InStockAffiliateProductPageProps } from "./in-stock-product-page";
import {
    DEFAULT_ACTIVE_BUY_OPTION,
    DEFAULT_PRODUCT,
    DEFAULT_SIZE,
    extractProductSizeData,
    getDefaultSizeList,
    getPromotion,
    getShippingInformation,
    getSizePrice,
} from "./utils";

export interface ShippingInformation {
    info: string;
    isFree: boolean;
}

export interface BuyOptionPromotionSerializer {
    tcs: {
        message: string;
        url: string;
    }[];
    message: string;
    url: string;
    promoCode: string;
}
export interface InStockProductPageContextProps {
    isRedesignPhaseTwo: boolean;
    country: string;
    isLoggedIn: boolean;
    termsUrl: string;
    designer: DesignerSerializer;
    product: ProductSerializer;
    buyOptions: BuyOptionSerializer[];
    sortedBuyOptionsKeys: string[];
    activeBuyOption: {
        buyOption: BuyOptionSerializer;
        defaultSize: SizeSerializer;
        promotion: BuyOptionPromotionSerializer | null;
    };
    activeProduct: {
        selectedSizeOption: SizeOption | null;
        productImageUrl: string;
        longDescription: string;
        price: {
            fullPrice: string | number;
            inStock: boolean;
            salePrice?: string | number;
        };
        cardSizeList: string[];
        shippingInformation: ShippingInformation[];
    };
    sizePicker: {
        sizes?: SizeOption[];
        isSizePickerExpanded: boolean;
        schemaLabels: Schemas;
        userDefaultSchema: string;
    };
    comparison: {
        isComparePriceOpen: boolean;
        openCompareModal: () => void;
        closeCompareModal: () => void;
    };
    relatedProductsSidebar?: RelatedProductsSidebarInterface | null;

    setSelectedSizeOption: React.Dispatch<React.SetStateAction<SizeOption | null>>;
    setIsSizePickerExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

export const InStockProductPageContext = createContext<InStockProductPageContextProps>({
    isRedesignPhaseTwo: false,
    country: "",
    isLoggedIn: false,
    termsUrl: "",
    designer: {
        id: 0,
        active: false,
        name: "",
        slug: "",
        url: "",
    },
    product: DEFAULT_PRODUCT,
    buyOptions: [],
    sortedBuyOptionsKeys: [],
    activeBuyOption: {
        buyOption: DEFAULT_ACTIVE_BUY_OPTION,
        defaultSize: DEFAULT_SIZE,
        promotion: null,
    },
    activeProduct: {
        selectedSizeOption: null,
        productImageUrl: "",
        longDescription: "",
        price: {
            fullPrice: "",
            inStock: false,
            salePrice: "",
        },
        cardSizeList: [],
        shippingInformation: [],
    },
    sizePicker: {
        sizes: [],
        isSizePickerExpanded: false,
        schemaLabels: {},
        userDefaultSchema: "",
    },
    comparison: {
        isComparePriceOpen: true,
        openCompareModal: noop,
        closeCompareModal: noop,
    },
    setSelectedSizeOption: noop,
    setIsSizePickerExpanded: noop,
    relatedProductsSidebar: null,
});

interface InStockProductPageContextProviderProps {
    children: React.ReactNode;
    initialData: Omit<InStockAffiliateProductPageProps, "helpCentreURLs">;
}

const InStockProductPageContextProvider = ({
    children,
    initialData,
}: InStockProductPageContextProviderProps): React.ReactElement => {
    const isRedesignPhaseTwo = Boolean(environment.getFeature("cw_pdp_redesign_phase_2"));
    const { productArea, productLayout } = initialData;
    const {
        designer,
        products,
        sorted_buy_options: sortedBuyOptionsKeys,
        buy_options: buyOptions,
        sorted_variants: sortedVariants,
        terms_url: termsUrl,
    } = productArea;
    const {
        product_size_picker: productSizesData,
        related_products_sidebar: relatedProductsSidebar,
    } = productLayout;
    const country = environment.get("country");
    const { isTabletViewport, isDesktopViewport } = useDomViewport();
    const isLoggedIn = useMemo(() => userProfiler.isLoggedIn(), []);
    const [selectedVariant] = useProductSelector(sortedVariants as string[]);

    const [isComparePriceOpen, setIsComparePriceOpen] = useState(false);

    function openCompareModal(): void {
        setIsComparePriceOpen(true);
    }

    function closeCompareModal(): void {
        setIsComparePriceOpen(false);
    }

    const product = products[selectedVariant];
    const buyOptionIds = [
        ...(sortedBuyOptionsKeys
            ? sortedBuyOptionsKeys.filter((linkId) => product.link_ids.includes(linkId))
            : []),
    ];

    const [selectedSizeOption, setSelectedSizeOption] = useState<SizeOption | null>(null);
    const activeLinkId = selectedSizeOption?.option_value?.link_id || buyOptionIds[0];
    const activeBuyOption = buyOptions[activeLinkId];

    const {
        sizes: activeBuyOptionSizes,
        default_size: defaultActiveSize,
        retailer_slug: retailerSlug,
    } = activeBuyOption;

    const defaultSize = activeBuyOptionSizes[defaultActiveSize] as SizeSerializer;
    const longDescription =
        retailerSlug in product.descriptions
            ? product.descriptions[retailerSlug]
            : product.descriptions["default"];

    const [isSizePickerExpanded, setIsSizePickerExpanded] = useState(false);

    const price = getSizePrice(defaultSize);
    const shippingInformation = getShippingInformation(activeBuyOption, defaultSize);

    const productImageUrl = product.images[0].full_size_url;
    const { sizes, sizeList, userDefaultSchema, schemaLabels } =
        extractProductSizeData(productSizesData);

    const cardSizeList = sizeList || getDefaultSizeList(activeBuyOptionSizes);
    const promotion = getPromotion(activeBuyOption, termsUrl);

    const value = {
        isRedesignPhaseTwo,
        country,
        isLoggedIn,
        termsUrl,
        designer,
        product,
        buyOptions,
        sortedBuyOptionsKeys: sortedBuyOptionsKeys as string[],
        activeBuyOption: {
            buyOption: activeBuyOption,
            defaultSize,
            promotion,
        },
        activeProduct: {
            selectedSizeOption,
            productImageUrl,
            longDescription,
            price,
            cardSizeList,
            shippingInformation,
        },
        sizePicker: {
            sizes,
            isSizePickerExpanded,
            schemaLabels,
            userDefaultSchema,
        },
        comparison: {
            isComparePriceOpen,
            openCompareModal,
            closeCompareModal,
        },
        setSelectedSizeOption,
        setIsSizePickerExpanded,
        relatedProductsSidebar:
            isTabletViewport || isDesktopViewport ? relatedProductsSidebar || null : null,
    };

    return (
        <InStockProductPageContext.Provider value={value}>
            {children}
        </InStockProductPageContext.Provider>
    );
};

export const useInStockProductPageContext = (): InStockProductPageContextProps =>
    useContext(InStockProductPageContext);

export default InStockProductPageContextProvider;
